import React, { useState } from 'react';

import Breadcrumbs from '@/components/common/Breadcrumbs';
import MaxWidth from '@/components/common/layout/MaxWidth';
import ValueProps from '@/components/common/ValueProps';
import DirectoryHero from '@/components/roadtrip-guides/DirectoryHero';
import css from '@/components/roadtrip-guides/RoadTripGuideFilters.module.css';
import Link from '@/components/switchback/Link/Link';
import { CMSPageData, CMSPageMetadata, CMSPageType } from '@/services/getCMSPageData';

type TRoadTrip = {
  audience: string;
  id: string;
  recommended_rig: string;
  title: string;
  trip_length: string;
  url: string;
};

interface RoadtripGuideDirectoryPageMetadata extends CMSPageMetadata {
  page_type: CMSPageType.EventGuideSubDirectory;
  roadtrips: TRoadTrip[];
}

export interface RoadtripGuideDirectoryPageData extends CMSPageData {
  metadata: RoadtripGuideDirectoryPageMetadata;
}

const RoadtripGuideDirectoryTemplate: React.FC<
  React.PropsWithChildren<RoadtripGuideDirectoryPageData>
> = props => {
  const { title, breadcrumbs, roadtrips } = props.metadata;
  const allLengths = ['1', '2-3', '3-5', '7+'];
  const allAudiences = ['all', 'single', 'family', 'couple', 'friends'];
  const allRigs = ['any', 'motorhome', 'van', 'trailer'];

  const [tripLengths, setTripLengths] = useState(allLengths);
  const [audiences, setAudiences] = useState(allAudiences);
  const [rigs, setRigs] = useState(allRigs);

  const filteredRoadTrips = filterRoadTrips({
    roadtrips,
    tripLengths,
    audiences,
    rigs,
  });

  return (
    <div className="flex flex-col items-center">
      <DirectoryHero
        title="Road Trip Guides"
        subTitle="Crisscross the country to see what lies between Points A and B"
      />

      <div className="justify-center hidden w-full py-2 bg-white border-b border-gray-200 md:flex">
        <div className="flex flex-col w-full px-4 max-w-xxl">
          <ValueProps />
        </div>
      </div>

      <MaxWidth className="mt-2">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </MaxWidth>

      <MaxWidth className="mt-12">
        <h2 className="mb-4 text-2xl font-medium">{title}</h2>
      </MaxWidth>

      <MaxWidth className="my-6">
        <div className="mb-2">
          Trip length:
          {allLengths.map(l => (
            <button
              key={l}
              className={`${css.button} ${tripLengths.includes(l) ? css.active : ''} mx-1`}
              onClick={() => {
                tripLengths.includes(l)
                  ? setTripLengths(tripLengths.filter(x => x !== l))
                  : setTripLengths([...tripLengths, l]);
              }}>
              {`${l} ${l === '1' ? 'day' : 'days'}`}
            </button>
          ))}
        </div>
        <div className="mb-2">
          Trip audience:
          {allAudiences.map(a => (
            <button
              key={a}
              className={`${css.button} ${audiences.includes(a) ? css.active : ''} mx-1`}
              onClick={() => {
                audiences.includes(a)
                  ? setAudiences(audiences.filter(x => x !== a))
                  : setAudiences([...audiences, a]);
              }}>
              {a}
            </button>
          ))}
        </div>
        <div className="mb-0">
          Recommended rig:
          {allRigs.map(r => (
            <button
              key={r}
              className={`${css.button} ${rigs.includes(r) ? css.active : ''} mx-1`}
              onClick={() => {
                rigs.includes(r) ? setRigs(rigs.filter(x => x !== r)) : setRigs([...rigs, r]);
              }}>
              {r}
            </button>
          ))}
        </div>
      </MaxWidth>
      <MaxWidth className="mt-4 mb-12">
        {filteredRoadTrips.length === 0 ? (
          <strong className="text-center">
            We&39;re sorry, there are currently no road trip guides that match your selected
            filters.
          </strong>
        ) : (
          <div className="grid grid-cols-3 gap-6">
            {filteredRoadTrips.map(roadtrip => (
              <RoadTrip key={roadtrip.url} roadtrip={roadtrip} />
            ))}
          </div>
        )}
      </MaxWidth>
    </div>
  );
};

const RoadTrip = ({ roadtrip }: { roadtrip: TRoadTrip }) => (
  <div>
    <h3 className={`text-lg font-medium ${css.roadTripLink}`}>
      <Link href={roadtrip.url} title={roadtrip.title}>
        {roadtrip.title}
      </Link>
    </h3>
    <ul>
      <li>Length: {roadtrip.trip_length} days</li>
      <li>Audience: {roadtrip.audience}</li>
      <li>Recommended Rig: {roadtrip.recommended_rig}</li>
    </ul>
  </div>
);

const filterRoadTrips = ({
  roadtrips = [],
  tripLengths = [],
  audiences = [],
  rigs = [],
}: {
  roadtrips: TRoadTrip[];
  tripLengths: string[];
  audiences: string[];
  rigs: string[];
}) => {
  const filteredRoadTrips = roadtrips
    .filter(r => tripLengths.map(l => l === r.trip_length).filter(Boolean)[0])
    .filter(r => audiences.map(a => a === r.audience).filter(Boolean)[0])
    .filter(r => rigs.map(x => x === r.recommended_rig).filter(Boolean)[0]);

  return filteredRoadTrips;
};

export default RoadtripGuideDirectoryTemplate;
